<template>
  <Modal ref="modalRef">
    <template #body>
      <View direction="column" gap="md" padding-block="md" align="center">
        <Text variant="h2">{{ hasUnits ? 'Remove its units first' : 'Delete this building?' }}</Text>
        <Text variant="body-large" color="secondary" align="center">{{ description }}</Text>
      </View>
    </template>
    <template #footer>
      <Button variant="ghost" @click="close">{{ hasUnits ? 'Close' : 'Cancel' }}</Button>
      <Button v-if="!hasUnits" :loading="isDeleting" data-testid="confirm-delete-building" @click="submit">
        Yes, delete
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import pluralize from 'pluralize';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { Button, Modal, Text, View, serverErrorCode, serverErrorDetail, useAlertsStore } from '@tallo/design-system';
import { portfolioRoute } from '@tallo/investor/navigation';
import { BuildingDto, buildingErrorCodes, useDeleteBuilding } from '@tallo/investor/property';
import { escapeHtml } from '@tallo/utility';

/** Mirrors the property's delete dialog; only an empty building goes, which the server checks too */
const props = defineProps<{ building: BuildingDto }>();

const router = useRouter();
const alertsStore = useAlertsStore();
const { mutateAsync: deleteBuilding } = useDeleteBuilding();
const modalRef = ref<InstanceType<typeof Modal>>();
const isDeleting = ref(false);
// Set when the server counted units this page had not seen yet
const refusedUnitCount = ref<number | null>(null);

const unitCount = computed(() => refusedUnitCount.value ?? props.building.unitCount);
const hasUnits = computed(() => unitCount.value > 0);
const description = computed(() =>
  hasUnits.value
    ? `It still has ${pluralize('unit', unitCount.value, true)}. A building goes once it is empty: delete its units first.`
    : 'This change is permanent.',
);

function open() {
  refusedUnitCount.value = null;
  modalRef.value?.show();
}

async function submit() {
  isDeleting.value = true;

  try {
    await deleteBuilding(props.building.id);
  } catch (error) {
    if (serverErrorCode(error) === buildingErrorCodes.buildingHasUnits) {
      refusedUnitCount.value = Math.max(1, Number(serverErrorDetail(error, 'unitCount')) || 0);

      return;
    }

    // A seat that can't change the building gets the access dialog instead, which holds this toast back
    alertsStore.catchError(error);
    close();

    return;
  } finally {
    isDeleting.value = false;
  }

  close();
  alertsStore.success(`Building <b>${escapeHtml(props.building.name)}</b> has been deleted`);
  router.push(portfolioRoute(undefined, 'buildings'));
}

function close() {
  modalRef.value?.close();
}

defineExpose({ open });
</script>
